import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import { statsLeadersFiltersSelector } from 'store/modules/statspro/stats-leaders/selectors/filters-selector';
import { UPDATE_FILTER_ITEM } from 'store/modules/statspro/stats-leaders/actions';
import { statsLeadersNavSelector } from 'store/modules/statspro/stats-leaders/selectors';
import {
    getTranslation,
    lookupTranslation
} from 'common/react/utils/translations';
import { SelectMultiple } from 'common/react/components/select-multiple';
import { SvgIcon } from 'common/react/components/SvgIcon';
import { useGetTeamsQuery } from 'store/modules/api/afl-api';
import { useFavouriteTeam } from 'common/react/hooks/use-favourite-team';

export const StatsLeadersTeamsFilters = ({
    addOnResetCallback,
    maximumSelections,
    counterThreshold
}) => {
    const dispatch = useDispatch();

    const filters = useSelector(statsLeadersFiltersSelector);
    const nav = useSelector(statsLeadersNavSelector);
    const { data: teamsList } = useGetTeamsQuery(nav.season.id, {
        skip: !nav.season.id ? true : false
    });

    const favouriteTeam = useFavouriteTeam();

    const teams = useMemo(() => {
        if (!teamsList) {
            return null;
        }

        const sortedTeams = [...teamsList].sort((a, b) =>
            a.name.localeCompare(b.name)
        );

        return favouriteTeam
            ? PULSE.app.common.team.moveFavouriteTeamToFront(
                  sortedTeams,
                  favouriteTeam
              )
            : sortedTeams;
    }, [teamsList, favouriteTeam]);

    const updateFilterItem = (filterItem, value) => {
        dispatch(UPDATE_FILTER_ITEM.request({ filterItem, value }));
    };

    return (
        <SelectMultiple
            items={teams}
            icon="guernsey"
            type="with-limit"
            selections={teams?.filter((t) =>
                filters.teams.includes(t.providerId)
            )}
            label={getTranslation('label.statsLeaders.filters.team')}
            title={getTranslation('label.statsLeaders.filters.team')}
            itemToString={(team) => team.name}
            onSelectedItemsChange={(team) => {
                updateFilterItem(
                    'teams',
                    team.map((t) => t.providerId)
                );
            }}
            addOnResetCallback={addOnResetCallback}
            getItemText={(item) => item.name}
            getItemLabel={(item) => (
                <span
                    style={{
                        alignItems: 'center',
                        display: 'flex',
                        justifyContent: 'space-between',
                        lineHeight: 1,
                        width: '100%'
                    }}
                    className={`${
                        item.favouriteTeam
                            ? 'select-multiple__favourite-team'
                            : ''
                    }`}
                >
                    <span style={{ marginRight: '1rem' }}>{item.name}</span>
                    <SvgIcon
                        icon={`aflc-${PULSE.app.common.team.getTeamAbbr(
                            item.providerId
                        )}-flag`}
                        className="icon"
                        style={{ width: '3.6rem', height: '3.6rem' }}
                    />
                </span>
            )}
            counterThreshold={counterThreshold}
            maximumSelections={maximumSelections}
            maximumSelectionsLabel={lookupTranslation(
                'label.filters.teams.maxSelected',
                { maximumSelections }
            )}
        />
    );
};

StatsLeadersTeamsFilters.propTypes = {
    addOnResetCallback: PropTypes.func,
    maximumSelections: PropTypes.number,
    counterThreshold: PropTypes.number
};
